import React, { useEffect, useState } from "react";
import i18next, { t } from "i18next";
import { getFaq } from "../../../api/getFaq";

// i18n lang → API lang
const LANG_MAP = { kz: "kk", ru: "ru", en: "en", cn: "zh" };

const getLang = () => LANG_MAP[i18next.language] || "ru";

const pick = (obj) => obj?.[getLang()] || obj?.ru || obj?.kk || "";

// ── Skeleton ──────────────────────────────────────────────────────────────────
const Skeleton = () => (
    <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((n) => (
            <div key={n} className="bg-white rounded-2xl p-5 space-y-3">
                <div className="h-4 bg-[#EDEDED] rounded w-1/3" />
                {[1, 2, 3].map((m) => (
                    <div key={m} className="h-12 bg-[#F9F9F9] rounded-xl" />
                ))}
            </div>
        ))}
    </div>
);

// ── Single accordion item ─────────────────────────────────────────────────────
const FaqItem = ({ question, answer }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className={`
            border border-[#EDEDED] rounded-xl overflow-hidden
            transition-all duration-200
            ${open ? "shadow-sm" : ""}
        `}>
            <button
                onClick={() => setOpen((v) => !v)}
                className="
                    w-full flex items-center justify-between gap-3
                    px-4 py-3.5 text-left bg-white
                    hover:bg-[#f9fdf9] transition-colors duration-150
                    cursor-pointer border-none outline-none
                "
            >
                <span className="text-sm font-medium text-[#222222] leading-snug">
                    {question}
                </span>
                <svg
                    className={`shrink-0 text-[#939393] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    width="18" height="18" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
                <div className="px-4 pb-4 pt-1 bg-white border-t border-[#EDEDED]">
                    <p className="text-sm text-[#939393] leading-relaxed m-0">{answer}</p>
                </div>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────
const Faq = () => {
    const [categories, setCategories] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        getFaq()
            .then((data) => setCategories(data.categories))
            .catch(() => setError(true));
    }, []);

    return (
        <section className="about_content">
            {/* Header */}
            <div className="content">
                <h1 className="m_0">{t("FAQ")}</h1>
            </div>

            {/* Body */}
            <div className="space-y-4 mt-0">
                {error && (
                    <div className="bg-white rounded-2xl p-6 text-center text-sm text-[#939393]">
                        {t("error") || "Ошибка загрузки"}
                    </div>
                )}

                {!error && !categories && <Skeleton />}

                {categories && Object.entries(categories).map(([key, cat]) => (
                    <div key={key} className="bg-white rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                        {/* Category name */}
                        <h2 className="text-[15px] font-semibold text-[#147F26] mb-3 mt-0">
                            {pick(cat.name)}
                        </h2>

                        {/* FAQ items */}
                        <div className="space-y-2">
                            {cat.faqs.map((faq) => (
                                <FaqItem
                                    key={faq.id}
                                    question={pick(faq.questions)}
                                    answer={pick(faq.answers)}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default Faq;
